import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown, Wrench } from "lucide-react";

export default function SalesLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const allowed = user.role === "admin" || user.can_view_sales;
  const [sales, setSales] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    if (!allowed) {
      navigate("/", { replace: true });
      return;
    }
    api.get("/sales").then((r) => setSales(r.data));
  }, [allowed, navigate]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return sales;
    return sales.filter(
      (s) =>
        s.customer?.toLowerCase().includes(q) ||
        s.mechanic_name?.toLowerCase().includes(q) ||
        s.vehicle?.toLowerCase().includes(q) ||
        s.items?.some((i) => i.name.toLowerCase().includes(q))
    );
  }, [sales, query]);

  const totalParts = (s) => s.items.reduce((n, i) => n + i.qty, 0);

  if (!allowed) return null;

  return (
    <div data-testid="sales-log-page">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
          <span className="h-px w-8 bg-primary/60" />
          PARTS &amp; CREW LOG <span className="font-jp text-muted-foreground">販売記録</span>
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">Sales Log</h1>
      </div>

      <div className="relative max-w-sm mb-5">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="sales-log-search"
          placeholder="Search employee, customer, vehicle or part…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="pl-9 bg-card border-border rounded-sm h-10 text-sm"
        />
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="sales-log-table">
        <div className="grid grid-cols-12 text-[10px] tracking-[0.2em] text-muted-foreground border-b border-border px-4 py-3">
          <span className="col-span-3 sm:col-span-2">DATE</span>
          <span className="col-span-3">EMPLOYEE</span>
          <span className="hidden sm:block col-span-3">CUSTOMER / VEHICLE</span>
          <span className="col-span-3 sm:col-span-2 text-right">PARTS</span>
          <span className="col-span-3 sm:col-span-2 text-right">TOTAL</span>
        </div>
        {filtered.map((s) => (
          <div key={s.id} className="border-b border-border/50 last:border-0" data-testid={`sales-log-row-${s.id}`}>
            <button
              data-testid={`sales-log-expand-${s.id}`}
              onClick={() => setOpen(open === s.id ? null : s.id)}
              className="w-full grid grid-cols-12 items-center px-4 py-3.5 text-xs hover:bg-secondary/30 transition-colors text-left"
            >
              <span className="col-span-3 sm:col-span-2 text-muted-foreground">
                {new Date(s.created_at).toLocaleDateString()}
                <span className="hidden sm:inline text-[10px] text-muted-foreground/60 ml-1.5">
                  {new Date(s.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </span>
              <span className="col-span-3 flex items-center gap-2 min-w-0">
                <span className="w-6 h-6 rounded-sm bg-secondary border border-primary/30 flex items-center justify-center text-primary shrink-0">
                  <Wrench size={11} />
                </span>
                <span className="text-foreground font-semibold truncate">{s.mechanic_name}</span>
                {s.mechanic_id === user.id && (
                  <span className="text-[8px] tracking-widest text-primary border border-primary/40 px-1 py-0.5 rounded-sm shrink-0">YOU</span>
                )}
              </span>
              <span className="hidden sm:flex col-span-3 items-center gap-2 text-muted-foreground truncate">
                <span className="truncate">
                  {s.customer}
                  {s.vehicle ? ` — ${s.vehicle}` : ""}
                </span>
                {s.sale_type === "pd_ems" && (
                  <span className="text-[8px] tracking-widest text-sky-400 border border-sky-400/40 px-1 py-0.5 rounded-sm shrink-0">PD/EMS</span>
                )}
              </span>
              <span className="col-span-3 sm:col-span-2 text-right text-muted-foreground">{totalParts(s)} part{totalParts(s) !== 1 ? "s" : ""}</span>
              <span className="col-span-3 sm:col-span-2 text-right flex items-center justify-end gap-2">
                <span className="text-foreground font-semibold">{fmt(s.total)}</span>
                <ChevronDown size={13} className={`text-muted-foreground transition-transform ${open === s.id ? "rotate-180 text-primary" : ""}`} />
              </span>
            </button>
            <AnimatePresence initial={false}>
              {open === s.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <div className="px-4 pb-4 pt-1 bg-secondary/20" data-testid={`sales-log-items-${s.id}`}>
                    <div className="text-[9px] tracking-[0.25em] text-muted-foreground mb-2">PARTS &amp; SERVICES USED</div>
                    <div className="space-y-1.5">
                      {s.items.map((i, idx) => (
                        <div key={idx} className="flex items-center justify-between text-xs border-b border-border/40 pb-1.5">
                          <span className="text-foreground">
                            <span className="text-primary font-semibold">{i.qty}×</span> {i.name}
                          </span>
                          <span className="text-muted-foreground">
                            {i.category} <span className="mx-1.5 text-border">|</span> <span className="text-foreground">{fmt(i.line_total)}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-[11px]">
                      <span className="text-muted-foreground">
                        Done by <span className="text-foreground font-semibold">{s.mechanic_name}</span>
                        {s.mechanic_rank ? ` (${s.mechanic_rank})` : ""}
                        {s.discount_percent > 0 && <span className="text-amber-400 ml-2">{s.discount_percent}% discount applied</span>}
                      </span>
                      <span className={s.commission_paid ? "text-emerald-400" : "text-amber-400"}>
                        {s.mechanic_name.split(" ")[0]}'s commission: {fmt(s.commission)} ({s.commission_rate}%) — {s.commission_paid ? "PAID" : "OWED"}
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
        {!filtered.length && (
          <div className="text-center text-muted-foreground text-xs py-14" data-testid="sales-log-empty">
            No sales match — the log fills up as the crew rings up jobs.
          </div>
        )}
      </div>
    </div>
  );
}
